/**
 * CenterlinesDisplayObject3D - Renders centerlines features using 3D paths
 *
 * Responsibilities:
 * - Create Path3DActors for each centerline
 * - Apply visualization properties (opacity, color, thickness, ...)
 * - Update rendering when properties change
 * - Register actors for picking (position resolution is handled by CurrentLocationTool)
 * - Clean up VTK objects on disposal
 */

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import type vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

import type { Centerline } from '../../domain';
import { BaseViewport } from '../../viewports';
import { Path3DActor } from '../actors/path-3d-actor';
import type { DisplayFeatureType, IDisplayFeature, IDisplayObject, ViewMode } from '../types';

export class CenterlinesDisplayObject3D implements IDisplayObject {
    readonly feature: IDisplayFeature<DisplayFeatureType.CENTERLINE>;
    readonly viewMode: ViewMode;

    private renderer: vtkRenderer | null = null;
    private centerlineActors = new Map<string, Path3DActor>();

    constructor(feature: IDisplayFeature<DisplayFeatureType.CENTERLINE>, viewMode: ViewMode) {
        this.feature = feature;
        this.viewMode = viewMode;
    }

    private get centerlinesData(): Map<string, Centerline> {
        return this.feature.data as Map<string, Centerline>;
    }

    initialize(viewport: BaseViewport): void {
        // Use layer 1 to ensure centerlines are visible
        this.renderer = viewport.getRenderer(1);

        const inactiveRadius = (this.feature.properties.tubeDiameter ?? 0.4) / 2;
        const centerlines = this.centerlinesData;
        for (const centerline of centerlines.values()) {
            const centerlinePoints = centerline.nodes.map((n) => n.point);
            const pathActor = new Path3DActor(centerlinePoints, inactiveRadius, [1, 1, 1]);

            if (pathActor.actor) {
                this.renderer.addActor(pathActor.actor!);
                this.centerlineActors.set(centerline.name, pathActor);
            }
        }

        this.applyTubeDiameters();
    }

    update(): void {
        this.applyTubeDiameters();
    }

    dispose(): void {
        // Remove path actors from renderers
        for (const pathActor of this.centerlineActors.values()) {
            if (pathActor.actor) {
                this.renderer?.removeActor(pathActor.actor!);
            }
            pathActor.dispose();
        }

        this.centerlineActors.clear();

        this.renderer = null;
    }

    private applyTubeDiameters(): void {
        const props = this.feature.properties;
        const inactiveRadius = (props.tubeDiameter ?? 0.4) / 2;
        const activeRadius = (props.activeTubeDiameter ?? 0.8) / 2;
        const activeId = props.activeCenterlineId ?? null;

        for (const [name, pathActor] of this.centerlineActors) {
            const radius = name === activeId ? activeRadius : inactiveRadius;
            pathActor.setRadius(radius);
        }
    }

    /**
     * Get all VTK actors created by this display object.
     */
    getActors(): vtkActor[] {
        const actors: vtkActor[] = [];
        for (const pathActor of this.centerlineActors.values()) {
            if (pathActor.actor) {
                actors.push(pathActor.actor);
            }
        }
        return actors;
    }
}
